import { styled } from "@linaria/react";
import classNames from "classnames";
import React from "react";
import ReactModal, { type Props as ReactModalProps } from "react-modal";
import { easingSoft, easingSoftMs, zIndex, radius } from "..";
// import ErrorBoundary from "../ErrorBoundary";
import LoadingBoundary from "../LoadingBoundary";
import { colors } from "../Theme";
import { shadows } from "../Theme/shadows";
import ModalHeader from "./Header";

export const MODAL_ID = "modal-id";

// if (process.env["NODE_ENV"] !== "test") ReactModal.setAppElement("#root");

const SMALL_MODAL_WIDTH = 500;
const MEDIUM_MODAL_WIDTH = 800;

const SMALL_CN = "small";
const MEDIUM_CN = "medium";
const AUTO_CN = "auto";

type LinariaModalProps = Omit<ReactModalProps, "className"> & { className?: string };

const StyledReactModal = styled(ReactModal as unknown as React.ComponentType<LinariaModalProps>)`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px 32px;

  will-change: opacity, transform;
  transition:
    opacity ${easingSoftMs}ms ${easingSoft},
    transform ${easingSoftMs}ms ${easingSoft};
  background: ${colors.hydrogen};
  outline: none;
  cursor: default;
  color: ${colors.carbon900};
  width: 100%;
  // used for full viewport chart (body -> none when active)
  pointer-events: auto;

  @media (min-width: ${SMALL_MODAL_WIDTH}px) and (min-height: 350px) {
    flex: none;
    align-self: flex-start;
    height: auto;
    max-height: calc(100% - 10vw);
    box-shadow: ${shadows.shadow400};
    border-radius: ${radius};
    margin: 5vw auto;

    &.${SMALL_CN} {
      width: ${SMALL_MODAL_WIDTH}px;
    }

    &.${MEDIUM_CN} {
      width: ${MEDIUM_MODAL_WIDTH}px;
    }

    &.${AUTO_CN} {
      max-width: fit-content;
      height: auto;
    }
  }
`;

export type ModalSize = "s" | "m" | "auto";

export interface ModalProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode | ((onRequestClose: () => void) => React.ReactNode);
  isOpen: boolean;
  closable?: boolean;
  onRequestClose: () => void;
  onAfterClose?: () => void;
  className?: string;
  size?: ModalSize;
}

const ModalV2 = ({
  title,
  subtitle,
  className,
  children,
  isOpen,
  closable = true,
  onRequestClose,
  onAfterClose,
  size = "s",
}: ModalProps) => {
  const [visible, setVisible] = React.useState(isOpen);
  const handleAfterClose = React.useCallback(() => {
    setVisible(false);
    onAfterClose?.();
  }, [onAfterClose]);
  React.useLayoutEffect(() => {
    if (isOpen) setVisible(true);
  }, [isOpen]);

  const style = React.useMemo(
    () => ({
      overlay: {
        willChange: "opacity",
        transition: `opacity ${easingSoftMs}ms ${easingSoft}`,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.modalOverlayBackground,
        cursor: closable ? "pointer" : "not-allowed",
        display: "flex",
        zIndex: zIndex.modalOverlay,
      } as const,
    }),
    [closable],
  );

  return (
    <StyledReactModal
      id={MODAL_ID}
      className={classNames(className, {
        [SMALL_CN]: size === "s",
        [MEDIUM_CN]: size === "m",
        [AUTO_CN]: size === "auto",
      })}
      closeTimeoutMS={easingSoftMs}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterClose={handleAfterClose}
      shouldCloseOnOverlayClick={closable}
      shouldCloseOnEsc={closable}
      style={style}
      ariaHideApp={false}
    >
      {typeof children === "function" ? (
        <LoadingBoundary>{visible ? children(onRequestClose) : null}</LoadingBoundary>
      ) : (
        <>
          <ModalHeader title={title} subtitle={subtitle} onRequestClose={onRequestClose} />
          <LoadingBoundary>{visible ? children : null}</LoadingBoundary>
        </>
      )}
    </StyledReactModal>
  );
};

export default React.memo(ModalV2);
